'use client';

import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { isAfter, parseISO } from 'date-fns';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { useAcceptTermsMutation } from 'src/api/redux';
import { useAuth } from 'src/context/auth';
import { useLogging } from 'src/context/logging';
import { Dialog } from 'src/components/PulseUI/Dialog';
import { PulseButton } from 'src/components/PulseUI/PulseButton';
import { Checkbox } from 'src/components/PulseUI/Checkbox';
import revision from './revision-content';
import agreementMarkdownComponents from './components/agreementMarkdownComponents';
import { TERMS_ENFORCED_SINCE } from './constants';

const TITLE_ID = 'terms-and-conditions-title';

const TermsAndConditionsModal = () => {
  const { user, setUser } = useAuth();
  const log = useLogging();
  const [hasAgreed, setHasAgreed] = useState(false);
  const [inlineError, setInlineError] = useState('');

  const [acceptTerms, { isLoading: isAcceptingTerms }] =
    useAcceptTermsMutation();

  if (!user) return null;

  const acceptedAt = user.company?.terms_accepted_at;
  const isOpen =
    !acceptedAt ||
    isAfter(parseISO(TERMS_ENFORCED_SINCE), parseISO(acceptedAt));

  const onAccept = async () => {
    setInlineError('');
    try {
      await acceptTerms({ termsVersion: revision.revisionName }).unwrap();

      setUser((oldUser) => ({
        ...oldUser,
        company: {
          ...oldUser?.company,
          terms_accepted_rev: revision.revisionName,
          terms_accepted_at: new Date().toISOString(),
        },
      }));

      log.event('termsAccepted', {
        revisionDate: revision.revisionDate,
        revisionName: revision.revisionName,
      });
    } catch (error) {
      setInlineError(
        error?.data?.message || 'Failed to accept terms. Please try again.'
      );
      log.exception(error);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={() => undefined}
      disableClickOutside
      size="xl"
      className="h-5/6 max-w-3xl"
      aria-labelledby={TITLE_ID}
      data-testid="terms-and-conditions-modal"
    >
      <Dialog.Header>
        <h2 id={TITLE_ID} className="text-lg font-medium">
          MedScout User Agreement
        </h2>
      </Dialog.Header>
      <Dialog.Content className="p-6">
        <div
          className="space-y-3 text-sm text-neutral-800"
          data-testid="terms-and-conditions-content"
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={agreementMarkdownComponents}
          >
            {revision.markdown}
          </ReactMarkdown>
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <div className="flex w-full flex-col gap-2">
          {inlineError && (
            <div className="text-sm text-red-600" role="alert">
              {inlineError}
            </div>
          )}
          <div className="flex items-center justify-between gap-4">
            <Checkbox
              checked={hasAgreed}
              onChange={(e) => setHasAgreed(e.target.checked)}
              label="I have read and agree to the MedScout User Agreement"
              data-testid="terms-and-conditions-agree"
            />
            <PulseButton
              variant="solid"
              color="primary"
              className="rounded-full"
              startDecorator={isAcceptingTerms ? faSpinner : undefined}
              disabled={!hasAgreed || isAcceptingTerms}
              onClick={onAccept}
              aria-label="Accept Terms and Conditions"
              data-testid="terms-and-conditions-accept"
            >
              {isAcceptingTerms ? 'Accepting...' : 'Accept'}
            </PulseButton>
          </div>
        </div>
      </Dialog.Actions>
    </Dialog>
  );
};

export default TermsAndConditionsModal;
